import React, { Component } from 'react';
import composeModalActions, { InjectedModalProps } from 'src/utils/modal-action-wrapper';
import { InjectedTranslateProps, StoreState } from 'src/types';
import {
    asyncRequest,
    Entities,
    EntityCollectionState,
    FetchBySearch,
    getEntities,
    search,
    TOMCAT_URL,
} from 'src/common';
import TableHelper from 'src/utils/table-helper';
import moment from 'moment';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { addErrorNotification, addNotification } from 'src/actions/notifications';
import { BootstrapTable, TableHeaderColumn, ToolBarProps } from 'react-bootstrap-table';
import { validFormatter } from 'src/utils/formatters';
import ErrorMessage from 'src/components/misc/error-message';
import DatePicker from 'material-ui/DatePicker';
import SelectField from 'material-ui/SelectField';
import FileSaver from 'file-saver';
import MenuItem from 'material-ui/MenuItem';
import { FilterToggle } from 'src/components/admin/buslocations/filter-toggle';
import BootstrapTableErrorBoundary from 'src/components/error/BootstrapTableErrorBoundary';
import FindMyBusRequestModal from 'src/components/admin/find-my-bus/components/find-my-bus-requests-modal';

interface TableDataRow {
    id: number;
    createdAt: string;
    bookingIntegrationId: number;
    email: string;
    taskFrom: string;
    fromTime: string;
    taskTo: string;
    requestState: string;
    journeyId: number;
    entity: any;
    note: string;
    openAIPositiveAnswer: boolean;
}

interface State {
    startedFetching: boolean;
    renderSelection?: boolean;
    selectedRow?: TableDataRow;
    fromDate?: Date;
    toDate?: Date;
    filterIntegrations: IntegrationOption[];
    selectedFilterIntegration: string;
    noDate: boolean;
    salesIntegration: boolean;
    openAIPositiveAnswer: boolean;
}

interface IntegrationOption {
    id: number; // The ID
    name: string; // The name/label
}

interface TableData extends Array<TableDataRow> {}

interface MapStateToProps {
    requests: EntityCollectionState<any>;
}

type Props = MapStateToProps & MapDispatchToProps & InjectedModalProps & InjectedTranslateProps;

interface MapDispatchToProps extends FetchBySearch {
    addNotification: (title: string, message: string, level: string, position: string) => void;
    addErrorNotification: (error: any) => void;
}

class FindMyBusRequestsTable extends Component<Props, State> {
    private tableHelper: any;
    private table: any;

    constructor(props: Props) {
        super(props);

        this.tableHelper = new TableHelper({
            onRowClick: this.onRowClick,
            pageSize: 100,
        });
        this.state = {
            startedFetching: false,
            fromDate: moment().startOf('month').toDate(),
            toDate: moment().toDate(),
            filterIntegrations: [],
            selectedFilterIntegration: '',
            noDate: false,
            salesIntegration: false,
            openAIPositiveAnswer: false,
        };
    }

    private onRowClick = (row: TableDataRow) => {
        this.tableHelper.setSelectedRow(row.id);
        this.setState({
            selectedRow: row,
        });
    };

    public componentWillMount() {
        this.fetchSearchRequests();
        this.fetchAvailableIntegrations();
    }

    private fetchSearchRequests = () => {
        const queryParams = [];
        if (this.state.fromDate && this.state.toDate) {
            queryParams.push(`fromDate=${moment(this.state.fromDate).format('YYYY-MM-DD')}`);
            queryParams.push(`toDate=${moment(this.state.toDate).format('YYYY-MM-DD')}`);
        }

        queryParams.push(`bookingIntegrationId=${this.state.selectedFilterIntegration}`);
        queryParams.push(`salesIntegration=${this.state.salesIntegration}`);

        this.props.search(
            Entities.FIND_MY_BUS_REQUEST,
            `get-requests?${this.createActiveFilterForQuery()}`,
            0,
            undefined,
            true,
            () => {
                this.setState({ startedFetching: false });
            },
        );
    };

    private createActiveFilterForQuery() {
        const queryParams = [];
        if (this.state.fromDate && this.state.toDate) {
            queryParams.push(`fromDate=${moment(this.state.fromDate).format('YYYY-MM-DD')}`);
            queryParams.push(`toDate=${moment(this.state.toDate).format('YYYY-MM-DD')}`);
        }

        queryParams.push(`bookingIntegrationId=${this.state.selectedFilterIntegration}`);
        queryParams.push(`salesIntegration=${this.state.salesIntegration}`);

        return queryParams.join('&');
    }

    private fetchAvailableIntegrations = () => {
        asyncRequest({ endpoint: `${TOMCAT_URL}api/admin/get-bookingIntegrationNames` })
            .then(response => {
                this.setState({ filterIntegrations: response.json });
            })
            .catch(error => {
                console.error('Error downloading intermediates', error);
            });
    };

    public componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.tableHelper.processPaginationAfterUpdate(prevProps.requests, this.props.requests, this.table))
            this.setState({ renderSelection: !this.state.renderSelection });

        if (
            prevState.fromDate !== this.state.fromDate ||
            prevState.toDate !== this.state.toDate ||
            prevState.selectedFilterIntegration !== this.state.selectedFilterIntegration ||
            prevState.noDate !== this.state.noDate ||
            prevState.salesIntegration !== this.state.salesIntegration
        ) {
            this.fetchSearchRequests();
        }
    }

    public render() {
        const { requests, t } = this.props;
        const tableData: TableData = [];

        const finishFetching = !this.state.startedFetching && !requests.isFetching;

        if (requests.error) return <ErrorMessage object={requests} />;

        if (!requests.isFetching && requests.items.length > 0) {
            requests.items.map((request: any) => {
                tableData.push({
                    id: request.id,
                    createdAt: moment(request.createdAt).utc().format('DD.MM.YYYY HH:mm:ss'),
                    bookingIntegrationId: request.bookingIntegrationId,
                    email: request.customerContactData != null ? request.customerContactData.email : null,
                    taskFrom: request.taskFrom,
                    fromTime: moment(request.taskFromTime).format('DD.MM.YYYY HH:mm'),
                    taskTo: request.taskTo,
                    requestState: request.searchResults,
                    journeyId: request.journeyId,
                    openAIPositiveAnswer: request.openAIPositiveAnswer,
                    note: request.note,
                    entity: request,
                });
            });
        }

        return (
            <div>
                <BootstrapTableErrorBoundary>
                    <BootstrapTable
                        ref={ref => (this.table = ref)}
                        data={tableData}
                        striped={true}
                        hover={true}
                        exportCSV={true}
                        condensed={true}
                        pagination={true}
                        options={Object.assign(
                            { toolBar: this.createCustomToolBar },
                            this.tableHelper.getOptions(finishFetching),
                        )}
                        selectRow={Object.assign({}, this.tableHelper.getRowProps())}
                        searchPlaceholder={t('common_phrases.search')}
                        search={true}>
                        <TableHeaderColumn columnTitle={true} dataField="id" hidden={true} isKey={true} export={true}>
                            ID
                        </TableHeaderColumn>
                        <TableHeaderColumn columnTitle={true} width="80" dataField="createdAt" dataSort={true}>
                            {t('find_my_bus.requested')}
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            columnTitle={true}
                            width="50"
                            dataField="bookingIntegrationId"
                            dataSort={true}
                            dataFormat={(cell: any) => validFormatter(t, cell != null)}>
                            {t('find_my_bus.bookingIntegration')}
                        </TableHeaderColumn>
                        <TableHeaderColumn width="100" dataField="email" dataSort={true}>
                            {t('search_requests.email')}
                        </TableHeaderColumn>
                        <TableHeaderColumn columnTitle={true} width="80" dataField="fromTime" dataSort={true}>
                            {t('search_requests.from.time')}
                        </TableHeaderColumn>
                        <TableHeaderColumn columnTitle={true} width="140" dataField="taskFrom" dataSort={true}>
                            {t('search_requests.from.location')}
                        </TableHeaderColumn>
                        <TableHeaderColumn columnTitle={true} width="140" dataField="taskTo" dataSort={true}>
                            {t('search_requests.to.location')}
                        </TableHeaderColumn>
                        <TableHeaderColumn columnTitle={true} width="140" dataField="requestState" dataSort={true}>
                            {t('find_my_bus.requestState')}
                        </TableHeaderColumn>
                        <TableHeaderColumn columnTitle={true} width="140" dataField="note" dataSort={true}>
                            {t('find_my_bus.customer_note')}
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="actions"
                            width="60"
                            dataFormat={this.tableActionsFormatter}
                            export={false}>
                            {t('common_phrases.actions')}
                        </TableHeaderColumn>
                    </BootstrapTable>
                </BootstrapTableErrorBoundary>
            </div>
        );
    }

    private createCustomToolBar = (props: ToolBarProps) => {
        const { t } = this.props;
        const { isFetching } = this.props.requests;

        return (
            <>
                <div className="col-md-12">
                    <div className="row">
                        <div className="col-md-12" style={{ display: 'flex', gap: 20, alignItems: 'flex-end' }}>
                            <div>
                                <FilterToggle
                                    label={t('find_my_bus.total_timespan')}
                                    labelStyle={{
                                        width: '250px',
                                        fontSize: '10px',
                                    }}
                                    toggled={this.state.noDate}
                                    onToggle={() => this.toggleDate(!this.state.noDate)}
                                />
                            </div>
                            <div style={{ marginRight: 'auto' }}>
                                <button type="button" className="btn btn-success" onClick={() => this.getCsv()}>
                                    {t('common_phrases.export_list')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12" style={{ display: 'flex', gap: 20, alignItems: 'flex-end' }}>
                            <div>
                                <FilterToggle
                                    label={t('find_my_bus.only_sales_integrations')}
                                    labelStyle={{
                                        width: '250px',
                                        fontSize: '10px',
                                    }}
                                    toggled={this.state.salesIntegration}
                                    onToggle={() => this.toggleBusfinderOnly()}
                                />
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                        <div>
                            <DatePicker
                                value={this.state.fromDate}
                                container="inline"
                                disabled={isFetching || this.state.noDate}
                                formatDate={date => moment(date).format('DD.MM.YYYY')}
                                floatingLabelText={t('search_requests.from.title')}
                                onChange={(_event: any, value: Date) => this.setState({ fromDate: value })}
                                maxDate={this.state.toDate}
                                autoOk={true}
                                textFieldStyle={{ width: 130, marginLeft: 7 }}
                            />
                        </div>
                        <div>
                            <DatePicker
                                value={this.state.toDate}
                                container="inline"
                                disabled={isFetching || this.state.noDate}
                                formatDate={date => moment(date).format('DD.MM.YYYY')}
                                floatingLabelText={t('search_requests.to.title')}
                                onChange={(_event: any, value: Date) => this.setState({ toDate: value })}
                                minDate={this.state.fromDate}
                                autoOk={true}
                                textFieldStyle={{ width: 130, marginLeft: 30 }}
                            />
                        </div>

                        <div className="col-md-3">
                            {this.state.filterIntegrations && (
                                <SelectField
                                    value={this.state.selectedFilterIntegration}
                                    onChange={(event, index, value) => {
                                        this.setState({
                                            selectedFilterIntegration: value,
                                        });
                                    }}
                                    floatingLabelText={t('find_my_bus.choose_booking_integration')}
                                    fullWidth={true}
                                    floatingLabelFixed={true}>
                                    <MenuItem value={null} primaryText={t('common_phrases.all')} />
                                    {this.state.filterIntegrations.map(item => (
                                        <MenuItem key={item.id} value={item.id} primaryText={item.name} />
                                    ))}
                                </SelectField>
                            )}
                        </div>

                        <div
                            style={{
                                width: 400,
                                marginRight: 6,
                                marginTop: 34,
                                marginLeft: 'auto',
                            }}>
                            {props.components.searchPanel}
                        </div>
                    </div>
                </div>
            </>
        );
    };

    private toggleBusfinderOnly = () => {
        this.setState({ salesIntegration: !this.state.salesIntegration });
    };

    private toggleDate = (val: boolean) => {
        this.setState({ noDate: !this.state.noDate });

        if (this.state.noDate) {
            this.setState({ fromDate: moment().startOf('month').toDate(), toDate: moment().toDate() });
        } else {
            this.setState({ fromDate: undefined, toDate: undefined });
        }
    };

    private getCsv = () => {
        asyncRequest({
            endpoint: `${TOMCAT_URL}api/${Entities.FIND_MY_BUS_REQUEST.repository}/get-csv?${this.createActiveFilterForQuery()}`,
        })
            .then(response => {
                response.blob().then((blob: Blob) => {
                    FileSaver.saveAs(blob, `find-my-bus-requests.csv`);
                });
            })
            .catch(error => {
                console.error('Error downloading intermediates', error);
            });
    };

    private tableActionsFormatter = (_cell: any, request: TableDataRow) => {
        const { t } = this.props;
        return (
            <div>
                <button
                    type="button"
                    className="btn btn-xs transparent"
                    title={t('user_functionality.open_in_route_planner')}
                    onClick={() => {
                        asyncRequest(
                            `${TOMCAT_URL}api/${Entities.FIND_MY_BUS_REQUEST.repository}/create-link/${request.journeyId}`,
                        )
                            .then(response => {
                                const json = response.json;
                                if (json && json.url) {
                                    const win = window.open(json.url, '_blank');
                                    if (win != null) win.focus();
                                }
                            })
                            .catch(error => console.error('Error creating Link', error));
                    }}>
                    <span className="glyphicon glyphicon-info-sign text-info" />
                </button>
                <button
                    type="button"
                    className="btn btn-xs transparent"
                    title={t('user_functionality.bus_companies_in_range')}
                    onClick={() => {
                        this.props.openModal({
                            component: FindMyBusRequestModal,
                            componentProps: {
                                id: request.id,
                                taskInfo: request,
                            },
                            title: t('find_my_bus.get_offers'),
                            width: '70%',
                            mandatoryFields: false,
                            singleButton: true,
                            cancelButtonLabel: t('find_my_bus.close_modal'),
                        });
                    }}>
                    <span className="glyphicon glyphicon-tasks" />
                </button>
            </div>
        );
    };
}

const mapStateToProps = (state: StoreState) => {
    return {
        requests: getEntities(state, Entities.FIND_MY_BUS_REQUEST),
    };
};

export default connect<any, any, any>(mapStateToProps, {
    push,
    search,
    addNotification,
    addErrorNotification,
})(composeModalActions(FindMyBusRequestsTable));
